import type Luminary from '@lumen5/luminary';
import type { AudioSourceData } from '@lumen5/luminary';

import { BaseRegistry, type ItemRepresentation } from './BaseRegistry';

import type { AssetContainerModel } from 'js/src/client';
import { AssetContainerTypes } from 'js/src/client';

interface MusicRepresentation extends ItemRepresentation {
    type: 'audio';
    duration: number;
}

export class MusicRegistry extends BaseRegistry<MusicRepresentation> {
    private static instance: MusicRegistry;

    public static getInstance(): MusicRegistry {
        if (!MusicRegistry.instance) {
            MusicRegistry.instance = new MusicRegistry();
        }
        return MusicRegistry.instance;
    }

    protected validateItemData(data: AssetContainerModel): void {
        if (!data.audio) {
            throw new Error('Invalid music data: must contain audio');
        }
    }

    protected extractItemInfo(data: AssetContainerModel): {
        title: string;
        description: string;
        tags: string[];
    } {
        const audio = data.audio!;
        return {
            title: audio.title || audio.name || 'Untitled',
            description: audio.description || '',
            tags: Array.isArray(audio.tags) ? audio.tags : [],
        };
    }

    protected createRepresentation(
        id: string,
        itemInfo: { title: string; description: string; tags: string[] },
        data: AssetContainerModel,
    ): MusicRepresentation {
        return {
            id,
            type: 'audio',
            title: itemInfo.title,
            description: itemInfo.description,
            tags: itemInfo.tags.slice(0, 10), // Limit tags to 10
            duration: Math.floor(data.audio!.duration),
        };
    }

    protected getItemUuid(data: AssetContainerModel): string | undefined {
        return data.audio?.uuid ?? undefined;
    }

    protected isItemAlreadyInRegistry(uuid: string): string | null {
        for (const id of this.registry.keys()) {
            const entry = this.registry.get(id);
            if (!entry) {
                continue;
            }
            if (entry.data.audio?.uuid === uuid) {
                return id;
            }
        }
        return null;
    }

    public getAudioSourceData(id: string): AudioSourceData | null {
        const entry = this.registry.get(id);
        if (!entry?.data.audio) {
            return null;
        }

        const audio = entry.data.audio;
        return {
            uuid: audio.uuid,
            source: audio.source,
            source_id: audio.source_id,
            license: audio.license,
            attribution: audio.attribution,
            mp3_url: audio.mp3_url,
            duration: audio.duration,
            description: audio.description,
            tags: audio.tags,
            title: audio.title,
            name: audio.name,
            quality: audio.quality,
            ai_voice: audio.ai_voice,
            ai_voice_speed: null,
        } as AudioSourceData;
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    public async addAnyUnseenItemsFromVideo(luminary: Luminary): Promise<void> {
        // Check if current music asset is not in registry
        const musicAudioAsset = luminary.musicAsset;
        if (
            musicAudioAsset?.sourceData?.uuid &&
            !this.getItemIdByUuid(musicAudioAsset.sourceData.uuid)
        ) {
            const sourceData = musicAudioAsset.sourceData;
            const audioItem: AssetContainerModel = {
                type: AssetContainerTypes.AUDIO,
                audio: {
                    uuid: sourceData.uuid,
                    source: sourceData.source,
                    source_id: sourceData.source_id,
                    license: sourceData.license,
                    attribution: sourceData.attribution,
                    mp3_url: sourceData.mp3_url,
                    duration: sourceData.duration,
                    description: sourceData.description || '',
                    tags: sourceData.tags || [],
                    title: sourceData.title || '',
                    name: sourceData.name || '',
                    quality: 0,
                    id: 0,
                    orig_mp3_url: '',
                    waveform: [
                        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                    ],
                    history_index_user_ids: null,
                    has_voiceover: null,
                    captions: null,
                    ai_voice: null,
                    ai_voice_speed: null,
                },
                background: null,
                icon: null,
                font: null,
                document: null,
                document_id: null,
                folder_id: null,
                article_id: null,
            };
            this.addItem(audioItem);
        }
    }
}

export type SerializedMusicRegistry = ReturnType<MusicRegistry['serialize']>;
